import { styled, useTheme } from "@mui/material/styles";
import { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  CommercialBookDataGrid,
  getUriIfSuccess,
  renderCorporateNumberWithClosedCaption,
  renderIndicatorIconButton,
} from "../../../components/DataGrid";
import {
  type GridColDef,
  type GridColumnHeaderParams,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { type CommercialBookStatus } from "../types";
import { useFeatureFlags } from "@/configs/featureFlag";
import { getCommercialBookErrorMessage } from "@/utils/commercialBookErrorMessage";

interface IProps {
  canModify?: boolean;
  row: CommercialBookStatus[] | undefined;
  /**
   * 解析対象として選択された行（id: CommercialPictureRequestDetail.id → 選択中かどうか）
   * feature flag（displayCheckboxCommercialPictureAnalyze）ONの場合のみ使用する。
   */
  selectedAnalyzeTargets: Map<number, boolean>;
  setSelectedAnalyzeTargets: React.Dispatch<
    React.SetStateAction<Map<number, boolean>>
  >;
}

export interface CommercialBookStatusDataGridRow {
  id: number;
  kubunType: string;
  prefectureName: string;
  location: string;
  status: string;
  corporateNumber: string;
  companyInfo: string;
  companyName: string;
  uri: string;
  closed: boolean;
  errorMessage: string;
  analyzeRequestId: number | null;
}

const StyledDataGrid = styled(CommercialBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

/**
 * 解析対象として選択可能な行かどうかを判定する（不動産 acquireBookStatus の existValidInfo 相当）。
 * status が SUCCEEDED、かつ既存の解析リクエストが無い行のみ選択可能。
 * closed（閉鎖謄本）は除外せず、他のSUCCEEDED行と同様に選択可能として扱う。
 */
export const isAnalyzeSelectable = (
  row: CommercialBookStatusDataGridRow
): boolean => row.status === "SUCCEEDED" && row.analyzeRequestId === null;

/**
 * 解析対象選択チェックボックス列を組み立てる（不動産 acquireBookStatus/components/FeedGrid.tsx の
 * allMattersInfoCheckBox 列相当）。テストから直接呼び出せるよう独立した関数として切り出す。
 */
const makeAnalyzeSelectColumn = (
  rows: CommercialBookStatusDataGridRow[],
  selectedAnalyzeTargets: Map<number, boolean>,
  setSelectedAnalyzeTargets: React.Dispatch<
    React.SetStateAction<Map<number, boolean>>
  >,
  selectAllAnalyzeTargets: boolean,
  setSelectAllAnalyzeTargets: React.Dispatch<React.SetStateAction<boolean>>
): GridColDef => {
  const handleSelectAllAnalyzeTargetsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newChecked = new Map<number, boolean>(selectedAnalyzeTargets);
    rows.forEach((row) => {
      if (!isAnalyzeSelectable(row)) return;
      newChecked.set(row.id, event.target.checked);
    });
    setSelectedAnalyzeTargets(newChecked);
    setSelectAllAnalyzeTargets(event.target.checked);
  };

  return {
    field: "analyzeSelect",
    headerName: "解析",
    width: 90,
    sortable: false,
    headerAlign: "center",
    align: "center",
    disableExport: true,
    renderCell: (
      params: GridRenderCellParams<string, CommercialBookStatusDataGridRow>
    ): React.ReactNode => {
      const targetRow = params.row;
      const selectable = isAnalyzeSelectable(targetRow);
      const disabledReason =
        targetRow.analyzeRequestId !== null
          ? "既に解析リクエスト済みです"
          : "取得が完了していません";
      return (
        <Tooltip title={selectable ? "" : disabledReason}>
          <span>
            <Checkbox
              checked={selectedAnalyzeTargets.get(targetRow.id) ?? false}
              onChange={(e) => {
                const newChecked = new Map<number, boolean>(
                  selectedAnalyzeTargets
                );
                newChecked.set(targetRow.id, e.target.checked);
                if (selectAllAnalyzeTargets) {
                  setSelectAllAnalyzeTargets(false);
                }
                setSelectedAnalyzeTargets(newChecked);
              }}
              disabled={!selectable}
            />
          </span>
        </Tooltip>
      );
    },
    renderHeader: (_params: GridColumnHeaderParams) => (
      <Checkbox
        checked={selectAllAnalyzeTargets}
        onChange={handleSelectAllAnalyzeTargetsChange}
        disabled={rows.every((row) => !isAnalyzeSelectable(row))}
        inputProps={{ "aria-label": "解析対象を全選択" }}
      />
    ),
  };
};

// カラム設定
export const makeGridColDef = (
  rows: CommercialBookStatusDataGridRow[],
  displayCheckboxCommercialPictureAnalyze: boolean,
  selectedAnalyzeTargets: Map<number, boolean>,
  setSelectedAnalyzeTargets: React.Dispatch<
    React.SetStateAction<Map<number, boolean>>
  >,
  selectAllAnalyzeTargets: boolean,
  setSelectAllAnalyzeTargets: React.Dispatch<React.SetStateAction<boolean>>
): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    {
      field: "kubunType",
      headerName: "区分",
      width: 120,
      sortable: false,
    },
    {
      field: "corporateNumber",
      headerName: "会社法人等番号",
      width: 200,
      sortable: false,
      renderCell: renderCorporateNumberWithClosedCaption,
    },
    {
      field: "location",
      headerName: "本店所在地",
      width: 400,
      sortable: false,
    },
    {
      field: "companyName",
      headerName: "商業・名称",
      width: 400,
      sortable: false,
    },
    {
      field: "uri",
      headerName: "商業登記",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: renderIndicatorIconButton,
    },
    {
      field: "errorMessage",
      headerName: "エラーメッセージ",
      width: 800,
      sortable: false,
    },
  ];

  // 既に解析リクエスト済みならチェックボックス列自体を非表示にする。
  const alreadyRequestedAnalyze = rows.some(
    (row) => row.analyzeRequestId !== null
  );

  if (!displayCheckboxCommercialPictureAnalyze || alreadyRequestedAnalyze) {
    return baseColumns;
  }

  return [
    makeAnalyzeSelectColumn(
      rows,
      selectedAnalyzeTargets,
      setSelectedAnalyzeTargets,
      selectAllAnalyzeTargets,
      setSelectAllAnalyzeTargets
    ),
    ...baseColumns,
  ];
};

// uriから最後のスラッシュと「法人登記簿」の間のテキスト(商業・名称)を抽出
const extractCompanyInfoFromUri = (value: CommercialBookStatus): string => {
  if (!value.downloadUrl) {
    return "";
  }
  const lastSlashIndex = value.downloadUrl.lastIndexOf("/");
  const lastPart = value.downloadUrl.substring(lastSlashIndex + 1);
  const keywordIndex = lastPart.indexOf("法人登記簿");
  return lastPart.substring(0, keywordIndex);
};

const convertRows = (
  rows: CommercialBookStatus[] | undefined
): CommercialBookStatusDataGridRow[] => {
  if (rows === undefined) return [];

  const result = rows.map((value): CommercialBookStatusDataGridRow => {
    return {
      id: value.id,
      kubunType: "商業及び法人",
      prefectureName: value.prefectureName,
      location: value.location,
      corporateNumber: value.corporateNumber ?? "",
      companyInfo: extractCompanyInfoFromUri(value),
      companyName: value.companyName ?? "",
      status: value.status as string,
      uri: getUriIfSuccess(value),
      closed: value.closed,
      errorMessage: getCommercialBookErrorMessage(value),
      analyzeRequestId: value.commercialPictureAnalyzeRequestId,
    };
  });
  return result;
};

/** 詳細の取得状況ラベル・チップ色 */
const DETAIL_STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: "成功",
  REQUESTING: "取得中",
  NEEDS_REVIEW: "取得結果を確認中",
  FAILED: "失敗",
};
const DETAIL_STATUS_COLORS: Record<
  string,
  "success" | "info" | "error" | "warning"
> = {
  SUCCEEDED: "success",
  REQUESTING: "info",
  NEEDS_REVIEW: "warning",
  FAILED: "error",
};

const DetailCardRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1.5,
      py: 0.25,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: "right", minWidth: 0, wordBreak: "break-all" }}>
      {children}
    </Box>
  </Box>
);

/**
 * SP（狭幅）向けの詳細カード一覧。テーブルは列が多く横スクロールになるため、
 * 1登記=1カードで縦に並べる。解析対象の選択チェックも各カード内に置く。
 */
const MobileDetailCards: React.FC<{
  rows: CommercialBookStatusDataGridRow[];
  showAnalyzeCheckbox: boolean;
  selectedAnalyzeTargets: Map<number, boolean>;
  setSelectedAnalyzeTargets: React.Dispatch<
    React.SetStateAction<Map<number, boolean>>
  >;
}> = ({
  rows,
  showAnalyzeCheckbox,
  selectedAnalyzeTargets,
  setSelectedAnalyzeTargets,
}) => {
  if (rows.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography>登記はありません</Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={1.5}>
      {rows.map((row) => {
        const isDownloadable = /^https?:/i.test(row.uri);
        const selectable = isAnalyzeSelectable(row);
        return (
          <Card key={row.id} variant="outlined">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.companyName || row.kubunType}
                </Typography>
                <Chip
                  label={DETAIL_STATUS_LABELS[row.status] ?? row.status}
                  size="small"
                  color={DETAIL_STATUS_COLORS[row.status] ?? "default"}
                  sx={{ flexShrink: 0 }}
                />
              </Box>
              <DetailCardRow label="区分">
                <Typography variant="body2">{row.kubunType}</Typography>
              </DetailCardRow>
              <DetailCardRow label="会社法人等番号">
                <Typography variant="body2">
                  {row.corporateNumber || "—"}
                  {row.closed ? "（閉鎖）" : ""}
                </Typography>
              </DetailCardRow>
              <DetailCardRow label="本店所在地">
                <Typography variant="body2">{row.location || "—"}</Typography>
              </DetailCardRow>
              {row.errorMessage ? (
                <DetailCardRow label="エラー">
                  <Typography variant="body2" color="error">
                    {row.errorMessage}
                  </Typography>
                </DetailCardRow>
              ) : null}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                {showAnalyzeCheckbox ? (
                  <Tooltip
                    title={
                      selectable
                        ? ""
                        : row.analyzeRequestId !== null
                        ? "既に解析リクエスト済みです"
                        : "取得が完了していません"
                    }
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              selectedAnalyzeTargets.get(row.id) ?? false
                            }
                            disabled={!selectable}
                            onChange={(e) => {
                              const next = new Map(selectedAnalyzeTargets);
                              next.set(row.id, e.target.checked);
                              setSelectedAnalyzeTargets(next);
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">解析対象</Typography>
                        }
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <span />
                )}
                {isDownloadable ? (
                  <Tooltip title="商業登記をダウンロード">
                    <IconButton
                      component={Link}
                      href={row.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      color="primary"
                      aria-label="商業登記をダウンロード"
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export const FeedGrid: React.FC<IProps> = (props) => {
  const { row, selectedAnalyzeTargets, setSelectedAnalyzeTargets } = props;
  const { displayCheckboxCommercialPictureAnalyze } = useFeatureFlags();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // 全選択チェックボックスの状態は不動産 acquireBookStatus/components/FeedGrid.tsx と同様、
  // このコンポーネント内のローカルstateとして持つ（選択内容自体は親から受け取る）。
  const [selectAllAnalyzeTargets, setSelectAllAnalyzeTargets] =
    useState<boolean>(false);

  // DataGridに表示できる形式に変換する
  const convertedRows = convertRows(row);

  const alreadyRequestedAnalyze = convertedRows.some(
    (r) => r.analyzeRequestId !== null
  );
  if (isMobile) {
    return (
      <MobileDetailCards
        rows={convertedRows}
        showAnalyzeCheckbox={
          Boolean(displayCheckboxCommercialPictureAnalyze) &&
          props.canModify !== false &&
          !alreadyRequestedAnalyze
        }
        selectedAnalyzeTargets={selectedAnalyzeTargets}
        setSelectedAnalyzeTargets={setSelectedAnalyzeTargets}
      />
    );
  }

  const colDef = makeGridColDef(
    convertedRows,
    Boolean(displayCheckboxCommercialPictureAnalyze) &&
      props.canModify !== false,
    selectedAnalyzeTargets,
    setSelectedAnalyzeTargets,
    selectAllAnalyzeTargets,
    setSelectAllAnalyzeTargets
  );

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={convertedRows}
      columns={colDef}
    />
  );
};
